import { useState, useEffect } from 'react'
import { showLoading } from 'services/util'

import adminCss from './sass/adminpage.module.scss'
import themeCss from '../components/sass/theme.module.scss'
import { getAuctionStatus } from 'sites/auctions/util/dateString'
import { isAuctionOwner } from './permissionsutil'
import { useMutation } from '@tanstack/react-query'
import { graphql } from 'sites/auctions/api'
import { updateAuctionMutation } from 'sites/auctions/graphql/Mutations'
import DonationDisplay from './DonationDisplay'
import DonationLinkCopyButton from './DonationLinkCopyButton'


export default function AdminDonations({ auction, refreshAdminAuctionQuery }) {
  // to handle the warning on loading with undefined values for inputs
  const inputValue = (value) => value ?? ''
  const toOptionalNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null
    return Number(value)
  }

  const [hasDonation, setHasDonation] = useState(auction?.HasDonation === true ? true : false)
  const [includeFundraisingGoal, setIncludeFundraisingGoal] = useState(auction?.IncludeFundraisingGoal === true ? true : false)
  const [fundraisingGoal, setFundraisingGoal] = useState(auction?.FundraisingGoal)
  const [lowDonationAmount, setLowDonationAmount] = useState(auction?.LowDonationAmount)
  const [midDonationAmount, setMidDonationAmount] = useState(auction?.MidDonationAmount)
  const [highDonationAmount, setHighDonationAmount] = useState(auction?.HighDonationAmount)

  const [disableEdit, setDisableEdit] = useState(false)
  
  const isUserSR = localStorage.getItem('isSolicitationReviewAdmin')
  const liveStatus = getAuctionStatus(auction?.StartTime, auction?.EndTime)
  let isReadOnly = ((auction.Status === 'CLOSED' || liveStatus !== 'before') && auction.Status !== 'PREVIEW') || !isAuctionOwner(auction) || isUserSR

  const hideWhenNoDonation = () => hasDonation === false ? adminCss['admin-donation-hidden'] : ''
  const hideWhenNoGoal = () => includeFundraisingGoal === false ? adminCss['admin-donation-hidden'] : ''

  const { data: updateAuctionResponse, error, isPending, mutate: saveAuction } = useMutation({
    mutationFn: (input) => {
      return graphql({ query: updateAuctionMutation, variables: { input: input } })
    },
  })

    useEffect(() => {
      showLoading(isPending)
  
      if (error) {
        console.log("Auction not created: ", error)
        console.log(error.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
      }
      else if (updateAuctionResponse) {
        console.log(updateAuctionResponse)
        console.log('Auction updated: ', updateAuctionResponse.data.updateAuction.AuctionId)
  
        refreshAdminAuctionQuery()
  
      }
    }, [updateAuctionResponse, error, isPending, refreshAdminAuctionQuery])
  

  useEffect(() => {
    const checkForNewValue = (val1, val2) => {
      if (val2 === null && val1 === undefined) return false
      if (val2 === null && val1 === '') return false
      return val1 !== val2
    }
    const checkForNewNumberValue = (val1, val2) => toOptionalNumber(val1) !== toOptionalNumber(val2)

    if (isReadOnly) setDisableEdit(true)
    else if (
      checkForNewValue(hasDonation, auction?.HasDonation) ||
      checkForNewValue(includeFundraisingGoal, auction?.IncludeFundraisingGoal) ||
      checkForNewNumberValue(fundraisingGoal, auction?.FundraisingGoal) ||
      checkForNewNumberValue(lowDonationAmount, auction?.LowDonationAmount) ||
      checkForNewNumberValue(midDonationAmount, auction?.MidDonationAmount) ||
      checkForNewNumberValue(highDonationAmount, auction?.HighDonationAmount)
    ) {
      setDisableEdit(false)
    } else setDisableEdit(true)

    // validate the donation elements 
    if (hasDonation && (
        (lowDonationAmount === '' || midDonationAmount === '' || highDonationAmount === '') ||
        (lowDonationAmount === null || midDonationAmount === null || highDonationAmount === null) ||
        (includeFundraisingGoal && (fundraisingGoal === '' || fundraisingGoal === null))
      )) {
        console.log('invalid donation values')
        setDisableEdit(true)
      }

  }, [auction, hasDonation, includeFundraisingGoal, fundraisingGoal, lowDonationAmount, midDonationAmount, highDonationAmount, isReadOnly])
  
  const onSubmit = async () => {

    const input = {
      AuctionId: auction.AuctionId,
      HasDonation: hasDonation,
      IncludeFundraisingGoal: includeFundraisingGoal,
      FundraisingGoal: toOptionalNumber(fundraisingGoal),
      LowDonationAmount: toOptionalNumber(lowDonationAmount),
      MidDonationAmount: toOptionalNumber(midDonationAmount),
      HighDonationAmount: toOptionalNumber(highDonationAmount),
    }
    console.log('input', input)
    saveAuction(input)
  }

  if (liveStatus !== 'before') return <DonationDisplay auction={auction} />

  return (
    <>
      <div className={adminCss['admin-donation-row']}>
        <h3 className={adminCss['admin-auction-page-tab-header']}>
          Donations
        </h3>
        <button className={`${themeCss['button']} ${themeCss['FilledBlue']} `} 
        onClick={() => onSubmit()} disabled={disableEdit}>
          Save Donation Settings
        </button>
      </div>

    
      <div className={adminCss['admin-auction-items-notices']}>
        <p>You can allow donations directly to the auction fund, and provide a fundraising goal.</p>
        <p>Attendees will be shown three suggested donation amounts, but will be able to enter a custom amount.</p>
      </div>
    

      <div className={adminCss['admin-donation-container']}>
        <div className={`${adminCss['admin-donation-questions']} ${adminCss['admin-donation-questions-stack']}`}>

        
          <div className={`${adminCss['admin-donation-row']} ${adminCss['admin-donation-form-row']}`}>
            <label className={adminCss['admin-donation-question-label']}>Allow donations?</label>
            <div className={`${adminCss['admin-donation-radiobuttons']} ${adminCss['admin-donation-radio-grid']}`}>
              <p className={adminCss['admin-donation-radio-option']}> <input type="radio" name="hasDonation" value="yes"
                    checked={hasDonation === true} disabled={isReadOnly}
                    onChange={() => setHasDonation(true)} />
                Yes
              </p>
              <p className={adminCss['admin-donation-radio-option']}> <input type="radio" name="hasDonation" value="no"
                    checked={hasDonation === false} disabled={isReadOnly}
                    onChange={() => setHasDonation(false)} />
                No
              </p>
            </div>


            {hasDonation === true && <div className={`${adminCss['admin-donation-amount-row']} ${adminCss['admin-donation-amount-grid']}`}>
          <div className={adminCss['admin-donation-input']}>
            <label>Low Suggestion</label>
            <input id="editAuctionLowDonationAmount" type='number'
              placeholder='e.g. 50'
              value={inputValue(lowDonationAmount)}
              disabled={isReadOnly}
              onChange={(e) => setLowDonationAmount(e.target.value)} />
          </div>

          <div className={adminCss['admin-donation-input']}>
            <label>Middle Suggestion</label>
            <input id="editAuctionMiddleDonationAmount" type='number'
              placeholder='e.g. 100'
              value={inputValue(midDonationAmount)}
              disabled={isReadOnly}
              onChange={(e) => setMidDonationAmount(e.target.value)} />
          </div>

          <div className={adminCss['admin-donation-input']}>
            <label>High Suggestion</label>
            <input id="editAuctionHighDonationAmount" type='number'
              placeholder='e.g. 250'
              value={inputValue(highDonationAmount)}
              disabled={isReadOnly}
              onChange={(e) => setHighDonationAmount(e.target.value)} />
          </div>
            </div>}

          </div>

        </div>

        <div className={`${adminCss['admin-donation-questions']} ${adminCss['admin-donation-questions-stack']}`}>
          <div className={`${adminCss['admin-donation-row']} ${adminCss['admin-donation-form-row']} ${hideWhenNoDonation()}`}>
            <label className={adminCss['admin-donation-question-label']}>Display a fundraising goal?</label>
            <div className={`${adminCss['admin-donation-goalanswers']} ${adminCss['admin-donation-goalanswers-grid']}`}>
              <div className={`${adminCss['admin-donation-radiobuttons']} ${adminCss['admin-donation-radio-grid']}`}>
                <p className={adminCss['admin-donation-radio-option']}> <input type="radio" name="includeFundraisingGoal" value="yes"
                      checked={includeFundraisingGoal === true} disabled={isReadOnly}
                      onChange={() => setIncludeFundraisingGoal(true)} />
                  Yes
                </p>

                <p className={adminCss['admin-donation-radio-option']}> <input type="radio" name="includeFundraisingGoal" value="no"
                      checked={includeFundraisingGoal === false} disabled={isReadOnly}
                      onChange={() => setIncludeFundraisingGoal(false)} />
                  No
                </p>
              </div>

              <div className={`${adminCss['admin-donation-input']} ${adminCss['admin-donation-goal-input']} ${hideWhenNoGoal()}`}>
                <label>Fundraising Goal</label>
                <input id="editAuctionFundraisingGoal" type='number'
                  placeholder='e.g. 10000'
                  value={inputValue(fundraisingGoal)}
                  disabled={isReadOnly}
                  onChange={(e) => setFundraisingGoal(e.target.value)} />
              </div>

            </div>
          </div>
        </div>
                
      </div>
        <div className={adminCss['admin-donation-copy-container']}>
          <div className={hideWhenNoDonation()}>
            <DonationLinkCopyButton auction={auction} />
          </div>
        </div>
    </>
  )
}
